<template>
  <form
    ref="form"
    novalidate
    class="container px-0 d-flex flex-column justify-content-between needs-validation"
    @submit="submit"
  >
    <div class="flex-grow-1">
      <div class="p-0">
        <div class="row">
          <BootstrapFormControl
            v-model="modelName"
            name="Product Name"
            class="col-8"
            required
            :error="errorMessages.get('name')"
            :server-error="serverError"
          />
          <BootstrapFormControl
            v-model="modelSkuPart"
            name="SKU Code"
            placeholder=""
            class="col-4"
            required
            :error="errorMessages.get('sku_part')"
            :server-error="serverError"
          />
        </div>
        <div class="row">
          <div class="col">
            <div class="card bg-body-secondary">
              <div class="card-body">
                <div class="container p-0">
                  <div class="row">
                    <BootstrapSelect
                      v-slot="{option}"
                      v-model="modelProductCategory"
                      class="col"
                      :options="productCategoryOptions.map((option) => option.value)"
                      name="Product Category"
                      required
                      :error="errorMessages.get('product_category_id')"
                      :server-error="serverError"
                    >
                      {{ productCategoryOptions.find(({value}) => value === option)?.label }}
                    </BootstrapSelect>
                    <BootstrapSelect
                      v-slot="{option}"
                      v-model="modelProductSubCategory"
                      class="col"
                      :options="productSubCategoryOptions.map((option) => option.value)"
                      name="Product Subcategory"
                      :disabled="productSubCategoryOptions?.length === 0"
                      required
                      :placeholder="
                        productSubCategoryOptions?.length === 0
                          ? 'No subcategories found'
                          : 'Please choose an option'
                      "
                      :error="errorMessages.get('product_sub_category_id')"
                      :server-error="serverError"
                    >
                      {{ productSubCategoryOptions.find(({value}) => value === option)?.label }}
                    </BootstrapSelect>
                    <BootstrapSelect
                      v-slot="{option}"
                      v-model="modelProductGroup"
                      class="col"
                      :options="productGroupOptions.map((option) => option.value)"
                      name="Product Group"
                      :disabled="productGroupOptions?.length === 0"
                      required
                      :placeholder="
                        productGroupOptions?.length === 0
                          ? 'No groups found'
                          : 'Please choose an option'
                      "
                      :error="errorMessages.get('product_group_id')"
                      :server-error="serverError"
                    >
                      {{ productGroupOptions.find(({value}) => value === option)?.label }}
                    </BootstrapSelect>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <BootstrapSelect
            v-model="modelProductType"
            name="Product Type"
            class="col-4"
            :options="productTypeOptions"
            required
            :disabled="productTypeOptions?.length === 0"
            :error="errorMessages.get('productOffering')"
            :server-error="serverError"
          />
          <div class="col-4">
            <label class="form-label">
              Region
              <span class="text-danger">*</span>
            </label>
            <div class="py-2">
              <BootstrapCheck
                v-for="region in regionsOptions"
                :id="region"
                :key="region"
                v-model="modelRegions"
                :required="modelRegions?.length === 0"
                :value="region"
                :label="region"
                inline
              />
            </div>
          </div>
        </div>
        <div class="row">
          <BootstrapSelect
            v-model="modelCurrentLifecycleStage"
            name="Lifecycle Stage"
            class="col"
            :options="[
              'In Development',
              'Production',
              'End of Sale',
              'End of Support',
              'End of Life',
            ]"
            required
            :error="errorMessages.get('current_lifecycle_stage')"
            :server-error="serverError"
          />
          <BootstrapDateTime
            v-model="modelCurrentLifecycleStartDate"
            name="Start Date"
            class="col"
            required
            :date-time="false"
            :error="errorMessages.get('current_lifecycle_start_date')"
            :server-error="serverError"
          />
          <BootstrapDateTime
            v-model="modelCurrentLifecycleEndDate"
            name="End Date"
            class="col"
            :date-time="false"
            :min="currentLifecycleStartDate"
            :error="errorMessages.get('current_lifecycle_end_date')"
            :server-error="serverError"
          />
        </div>
        <div class="row">
          <BootstrapFormControl
            v-model="modelDescription"
            name="Description"
            class="col"
            textarea
            required
            :error="errorMessages.get('description')"
            :server-error="serverError"
          />
        </div>
      </div>
    </div>
    <div class="d-flex w-100">
      <BootstrapButton
        name="submit"
        class="me-2"
        type="submit"
        :disabled="loading"
        :loading="loading"
        @click.stop
      >
        Save
      </BootstrapButton>
      <BootstrapButton name="cancel" variant="secondary" @click="router.go(-1)">
        Cancel
      </BootstrapButton>
    </div>
  </form>
</template>
<script lang="ts" setup>
const props = defineProps<{
  description: string;
  errorMessages: Map<any, any>;
  loading: boolean;
  name: string;
  productCategory: number | string;
  productCategoryOptions: {label: string; value: number}[];
  productSubCategory: number | string;
  productSubCategoryOptions: {label: string; value: number}[];
  productGroup: number | string;
  productGroupOptions: {label: string; value: number}[];
  productType: string;
  productTypeOptions: string[];
  currentLifecycleStage: string;
  currentLifecycleStartDate: string;
  currentLifecycleEndDate: string;
  regions: string[];
  regionsOptions: string[];
  serverError: boolean;
  skuPart: string;
}>();

const emit = defineEmits<{
  'update:description': [value: string];
  'update:name': [value: string];
  'update:productCategory': [value: number | string];
  'update:productSubCategory': [value: number | string];
  'update:productGroup': [value: number | string];
  'update:productType': [value: string];
  'update:currentLifecycleStage': [value: string];
  'update:currentLifecycleStartDate': [value: string];
  'update:currentLifecycleEndDate': [value: string];
  'update:regions': [value: string[]];
  'update:skuPart': [value: string];
  submit: [form: globalThis.Ref<HTMLFormElement | null>];
}>();

const router = useRouter();

// Template ref
const form = ref<HTMLFormElement | null>(null);

// Models
const modelDescription = computed({
  get: () => props.description,
  set: (value) => emit('update:description', value),
});
const modelName = computed({
  get: () => props.name,
  set: (value) => emit('update:name', value),
});
const modelProductCategory = computed({
  get: () => props.productCategory as string,
  set: (value) => emit('update:productCategory', value),
});
const modelProductSubCategory = computed({
  get: () => props.productSubCategory as string,
  set: (value) => emit('update:productSubCategory', value),
});
const modelProductGroup = computed({
  get: () => props.productGroup as string,
  set: (value) => emit('update:productGroup', value),
});
const modelProductType = computed({
  get: () => props.productType,
  set: (value) => emit('update:productType', value),
});
const modelCurrentLifecycleStage = computed({
  get: () => props.currentLifecycleStage,
  set: (value) => emit('update:currentLifecycleStage', value),
});
const modelCurrentLifecycleStartDate = computed({
  get: () => props.currentLifecycleStartDate,
  set: (value) => emit('update:currentLifecycleStartDate', value),
});
const modelCurrentLifecycleEndDate = computed({
  get: () => props.currentLifecycleEndDate,
  set: (value) => emit('update:currentLifecycleEndDate', value),
});
const modelRegions = computed({
  get: () => props.regions,
  set: (value) => emit('update:regions', value),
});
const modelSkuPart = computed({
  get: () => props.skuPart,
  set: (value) => emit('update:skuPart', value),
});

// https://getbootstrap.com/docs/4.6/components/forms/?#how-it-works
const submit = async (event: Event) => {
  if (form.value?.checkValidity() === false) {
    event.preventDefault();
    event.stopPropagation();
    form.value.classList.add('was-validated');
  } else {
    event.preventDefault();
    emit('submit', form);
  }
};
</script>
